import React, { useContext, useRef, useState } from 'react';
import { TranslationKey } from './../../../../types/translation/TranslationKey';
import { t, translate } from '../../../../types/translation/Translator';
import { useNavigate } from 'react-router-dom';
import ProductMasterDataInfoPane from './Panes/ProductMasterDataInfoPane';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { CompanyContext } from '../../../../context/CompanyContext';
import {
  CreateProductMasterDataResponse,
  CreateProductMasterDataVariables,
  ProductMasterDataMutations,
} from '../../../../graphql/productMasterData.graphql';
import { ProductMasterDataContext } from '../../../../context/ProductMasterDataContext';
import { Routing } from '../../../../util/routes.util';
import { CreateFilesResponse, CreateFilesVariables, FileMutations } from '../../../../graphql/file.graphql';
import { CreateFileInput, FileEntity, FileEntityType } from '../../../../types/file';
import { FileContext } from '../../../../context/FileContext';
import { UploadFileInput } from '../../../../VentoryUI/components/common/File';
import { FileItem } from '../../../components/File';
import { TagMultiSelect } from '../../../components/Dropdown';
import { EntityType } from '../../../../types/comment';
import {
  CreateTagRelationResponse,
  CreateTagRelationVariables,
  TagRelationMutations,
} from '../../../../graphql/tagRelation.graphql';
import { TagRelation } from '../../../../types/tag/tagRelation';
import { TagRelationContext } from '../../../../context/TagRelationContext';
import useMutation from '../../../../hooks/useMutation';
import { useToast } from '../../../../providers/ToastProvider';
import { ToastType } from '../../../components/Toast';
import { BackButton, CreateButton } from '../../../components/Button';
import { ProductMasterDataInput } from '../../../../types/product_master_data';
import ProductMasterDataOverviewCrumb from '../../../components/Breadcrumbs/crumbs/ProductMasterDataOverviewCrumb';
import { Breadcrumbs, Crumb } from '../../../components/Breadcrumbs';
import { v4 } from 'uuid';

export default function CreateProductMasterDataPane({}) {
  const navigate = useNavigate();

  const { currentCompany } = useContext(CompanyContext);
  const { productMasterData, setProductMasterData } = useContext(ProductMasterDataContext);
  const { files, setFiles } = useContext(FileContext);
  const { tagRelations, setTagRelations } = useContext(TagRelationContext);

  const [filesToCreate, setFilesToCreate] = useState<Map<string, File>>(new Map());
  const [selectedTagIds, setSelectedTagIds] = useState(new Set<string>());
  const [productMasterDataInput, setProductMasterDataInput] = useState(
    new ProductMasterDataInput({ companyId: currentCompany.id }),
  );

  const [create, { loading: createPmdLoading }] = useMutation<
    CreateProductMasterDataResponse,
    CreateProductMasterDataVariables
  >(ProductMasterDataMutations.create, {
    onCompleted: response => {
      const pmd = response.createProductMasterData[0];
      setProductMasterData(new Map(productMasterData).set(pmd.id, new ProductMasterData(pmd)));
    },
  });

  const [createFile, { loading: createFileLoading }] = useMutation<CreateFilesResponse, CreateFilesVariables>(
    FileMutations.create,
    {
      onCompleted: response => {
        response.createFiles.forEach(file => files.set(file.id, new FileEntity(file)));
        setFiles(new Map(files));
      },
    },
  );

  const [createTagRelation, { loading: createTagRelationLoading }] = useMutation<
    CreateTagRelationResponse,
    CreateTagRelationVariables
  >(TagRelationMutations.create, {
    onCompleted: response => {
      response.createTagRelation.forEach(tr => tagRelations.set(tr.id, tr));
      setTagRelations(new Map(tagRelations));
    },
  });

  const handleCreate = async () => {
    const pmdResponse = await create({
      variables: {
        productMasterData: [productMasterDataInput.forCreate()],
      },
    });
    const pmdId = pmdResponse.data?.createProductMasterData[0].id;
    if (!pmdId) return;

    if (filesToCreate.size) {
      await createFile({
        variables: {
          files: await Promise.all(
            Array.from(filesToCreate.entries()).map(async ([id, file]) => {
              const fileInput: CreateFileInput = await CreateFileInput.fromFile(
                file,
                pmdId,
                FileEntityType.pmd,
                currentCompany.id,
              );
              fileInput.id = id;
              return fileInput;
            }),
          ),
        },
      });
    }

    if (selectedTagIds.size) {
      await createTagRelation({
        variables: {
          tagRelations: [...selectedTagIds].map(tagId =>
            new TagRelation({ companyId: currentCompany.id })
              .withEntityId(pmdId)
              .withTagId(tagId)
              .withType(EntityType.stockLocation)
              .forCreate(),
          ),
        },
      });
    }

    navigate(Routing.productMasterData.overview());
  };

  const loading = createPmdLoading || createFileLoading || createTagRelationLoading;

  return (
    <div className='flex h-full flex-col overflow-hidden'>
      <Breadcrumbs>
        <ProductMasterDataOverviewCrumb />
        <Crumb
          current
          path={Routing.productMasterData.create()}
          text={translate({ key: TranslationKey.createProductMasterData })}
        />
      </Breadcrumbs>

      <div className='content-y-breadcrumbs flex flex-grow flex-col gap-8 overflow-hidden'>
        <ProductMasterDataInfoPane
          input={productMasterDataInput}
          setInput={setProductMasterDataInput}
          filesInput={filesToCreate}
          uploadFilesComponent={
            <FileUploadManagement filesToCreate={filesToCreate} setFilesToCreate={setFilesToCreate} />
          }
          tagInput={
            <TagMultiSelect
              selectedTagIds={selectedTagIds}
              entityType={EntityType.pmd}
              onTagSelected={tagId => {
                setSelectedTagIds(new Set(selectedTagIds).add(tagId));
              }}
              onTagCreated={tag => {
                setSelectedTagIds(new Set(selectedTagIds).add(tag.id));
              }}
              onTagRemoved={(tagId: string) => {
                selectedTagIds.delete(tagId);
                setSelectedTagIds(new Set(selectedTagIds));
              }}
            />
          }
        />
        <div className='content-x flex gap-2'>
          <BackButton onClick={() => navigate(Routing.productMasterData.overview())} disabled={loading} />
          <CreateButton
            loading={loading}
            disabled={!productMasterDataInput.productName || !productMasterDataInput.productNumber}
            onClick={() => handleCreate()}
          />
        </div>
      </div>
    </div>
  );
}

interface FileUploadManagementProps {
  filesToCreate: Map<string, File>;
  setFilesToCreate: React.Dispatch<React.SetStateAction<Map<string, File>>>;
}

function FileUploadManagement({ filesToCreate, setFilesToCreate }: FileUploadManagementProps) {
  const { showToast } = useToast();

  const filesListRef = useRef<HTMLDivElement>(null);

  const handleFileAdd = (files: FileList | null) => {
    if (!files || !files.length) return;

    const uploadedFiles = Array.from(files);
    if (!filesToCreate) {
      setFilesToCreate(new Map(uploadedFiles.map(file => [v4(), file])));
      return;
    }

    const existingFileNames = new Set([...filesToCreate.values()].map(file => file.name));

    const duplicateFiles: Pick<File, 'name'>[] = [];
    const newFiles = uploadedFiles.filter(f => {
      if (existingFileNames.has(f.name)) {
        duplicateFiles.push({ name: f.name });
        return false;
      }
      return true;
    });

    if (duplicateFiles.length) {
      const duplicateFileNames = duplicateFiles.map(f => f.name).join(', ');
      const messagePluralization =
        duplicateFiles.length === 1
          ? `${t(TranslationKey.fileExistsToastErrorMessage).singular.label}`
          : `${t(TranslationKey.fileExistsToastErrorMessage).plural.label}`;
      showToast({ type: ToastType.error, title: `${messagePluralization}: ${duplicateFileNames}` });
    }

    const mergedFiles = new Map(filesToCreate);
    newFiles.forEach(file => mergedFiles.set(v4(), file));
    setFilesToCreate(mergedFiles);
  };

  const handleFileRemove = (id: string) => {
    const updatedFiles = new Map(filesToCreate);
    updatedFiles.delete(id);
    setFilesToCreate(updatedFiles);
  };

  return (
    <div className='flex flex-col gap-2'>
      <UploadFileInput
        fileEntityType={FileEntityType.pmd}
        multiple={true}
        height='232px'
        loading={false}
        onFile={handleFileAdd}
      />
      <div className='flex flex-col gap-2'>
        {filesToCreate
          ? [...filesToCreate.entries()].map(([id, file], i) => (
              <div key={`${file.name}-${i}`} className='w-full'>
                <FileItem onRemove={() => handleFileRemove(id)} file={file} />
              </div>
            ))
          : null}
      </div>
    </div>
  );
}
